import React, { useState, useCallback, Fragment } from "react";
import { Formik, Field, Form } from "formik";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getChangedDocumentsModuleData } from "config/changesDocumentData";
import _get from "lodash/get";
import Checkbox from "components/lib/Shared/Checkbox";
import { Constants } from "config/constants";

/**
 * ChangeDocuments component handles the display, upload, and download of
 * unsigned and signed change documents for company officials.
 *
 * Props:
 * - row: The data row containing the official and document info
 * - isViewMode: Boolean indicating if the component is in view mode
 */
const ChangeDocuments = ({
  row,
  isViewMode,
  handleSaveComment,
  companyOfficialEffectKey,
  officialSliceName,
  isCompanyDashboard,
  changeDocumentsComments,
  isReviewMode,
}) => {
  let changeDocuments = [];

  const isAppointment =
    row?.original?.changeType ===
    Constants.TRANSACTION_CHANGE_TYPES.APPOINTMENT;
  const isCessation =
    row?.original?.changeType === Constants.TRANSACTION_CHANGE_TYPES.CESSATION;
  const isChangeOfParticulars =
    row?.original?.changeType ===
    Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS;

  const unsignedChangeOfParticularsDocs = {
    statutoryDeclaration:
      row?.original?.uploadChangeDocuments?.changeOfParticulars
        ?.statutoryDeclaration?.convertedPdf,
  };

  const unsignedCessationDocs = {
    letterOfResignation:
      row?.original?.uploadChangeDocuments?.cessation?.letterOfResignation
        ?.convertedPdf,
    statutoryDeclaration:
      row?.original?.uploadChangeDocuments?.cessation?.statutoryDeclaration
        ?.convertedPdf,
  };

  const unsignedAppointmentDocs = {
    consentToAct:
      row?.original?.appointment?.changeOfParticulars?.consentToAct
        ?.convertedPdf,
    appointmentLetter:
      row?.original?.appointment?.changeOfParticulars?.appointmentLetter
        ?.convertedPdf,
  };

  const signedChangeOfParticularsDocs = {
    statutoryDeclaration:
      row?.original?.uploadChangeDocuments?.changeOfParticulars
        ?.statutoryDeclaration?.templateFile?.modeType ===
      "pre-signed-documents"
        ? row?.original?.uploadChangeDocuments?.changeOfParticulars
            ?.statutoryDeclaration?.templateFile?.fileName
        : row?.original?.uploadChangeDocuments?.changeOfParticulars
            ?.statutoryDeclaration?.signedPdf,
  };

  const signedCessationDocs = {
    letterOfResignation:
      row?.original?.uploadChangeDocuments?.cessation?.letterOfResignation
        ?.templateFile?.modeType === "pre-signed-documents"
        ? row?.original?.uploadChangeDocuments?.cessation?.letterOfResignation
            ?.templateFile?.fileName
        : row?.original?.uploadChangeDocuments?.cessation?.letterOfResignation
            ?.signedPdf,
    statutoryDeclaration:
      row?.original?.uploadChangeDocuments?.cessation?.statutoryDeclaration
        ?.templateFile?.modeType === "pre-signed-documents"
        ? row?.original?.uploadChangeDocuments?.cessation?.statutoryDeclaration
            ?.templateFile?.fileName
        : row?.original?.uploadChangeDocuments?.cessation?.statutoryDeclaration
            ?.signedPdf,
  };

  const signedAppointmentDocs = {
    consentToAct:
      row?.original?.appointment?.appointment?.consentToAct?.templateFile
        ?.modeType === "pre-signed-documents"
        ? row?.original?.appointment?.appointment?.consentToAct?.templateFile
            ?.fileName
        : row?.original?.appointment?.appointment?.consentToAct?.signedPdf,
    appointmentLetter:
      row?.original?.appointment?.appointment?.appointmentLetter?.templateFile
        ?.modeType === "pre-signed-documents"
        ? row?.original?.appointment?.appointment?.appointmentLetter
            ?.templateFile?.fileName
        : row?.original?.appointment?.appointment?.appointmentLetter?.signedPdf,
  };

  let unsignedFileUploads = {};
  let signedFileUploads = {};
  let documentData = [];
  let uploadChangeDocuments = {};
  let changeType = "";

  if (isChangeOfParticulars) {
    unsignedFileUploads = {
      ...unsignedChangeOfParticularsDocs,
    };
    signedFileUploads = {
      ...signedChangeOfParticularsDocs,
    };
    documentData = getChangedDocumentsModuleData(
      "changeOfParticularsChangeDocuments"
    );
    uploadChangeDocuments = {
      changeOfParticulars:
        row?.original?.uploadChangeDocuments?.changeOfParticulars,
    };
    changeType = "changeOfParticulars";
  } else if (isCessation) {
    unsignedFileUploads = {
      ...unsignedCessationDocs,
    };
    signedFileUploads = {
      ...signedCessationDocs,
    };
    documentData = getChangedDocumentsModuleData("cessationChangeDocuments");
    uploadChangeDocuments = {
      cessation: row?.original?.uploadChangeDocuments?.cessation,
    };
    changeType = "cessation";
  } else if (isAppointment) {
    unsignedFileUploads = {
      ...unsignedAppointmentDocs,
    };
    signedFileUploads = {
      ...signedAppointmentDocs,
    };
    documentData = getChangedDocumentsModuleData("appointmentChangeDocuments");
    uploadChangeDocuments = {
      appointment: row?.original?.uploadChangeDocuments?.appointment,
    };
    changeType = "appointment";
  }

  Object.keys(uploadChangeDocuments).forEach((key) => {
    if (
      uploadChangeDocuments[key] &&
      Object.keys(uploadChangeDocuments[key]).length > 0
    ) {
      Object.keys(uploadChangeDocuments[key]).forEach((docKey) => {
        changeDocuments.push({
          [docKey]: uploadChangeDocuments[key][docKey],
        });
      });
    }
  });

  return (
    <>
      {changeDocuments.map((changeDoc) => (
        <div className="py-4 mt-2">
          <div className="flex flex-col py-4 px-2">
            <h3 className="font-semibold text-slate-800">Document Selected:</h3>
            <div className="flex px-2 py-2 space-x-2">
              <Checkbox
                name="Signed Documents"
                label="Signed Documents"
                checked={
                  changeDoc[Object.keys(changeDoc)[0]]["templateFile"][
                    "modeType"
                  ] === "pre-signed-documents"
                }
                disabled
              />
              <Checkbox
                name="Not Signed Documents"
                label="Not Signed Documents"
                checked={
                  changeDoc[Object.keys(changeDoc)[0]]["templateFile"][
                    "modeType"
                  ] === "not-signed-documents"
                }
                disabled
              />
            </div>
          </div>
          <div className="flex gap-5">
            {changeDoc[Object.keys(changeDoc)[0]]["templateFile"][
              "modeType"
            ] === "not-signed-documents" && (
              <>
                {/* Unsigned document section */}
                <div className="w-full">
                  <div className="px-2">Unsigned version</div>
                  <div className="p-3 mt-2 bg-white">
                    <Formik
                      enableReinitialize
                      initialValues={{
                        fileUploads: {
                          [Object.keys(changeDoc)[0]]:
                            changeDoc[Object.keys(changeDoc)[0]]?.templateFile
                              .fileName,
                        },
                      }}
                      onSubmit={() => null}
                    >
                      {() => (
                        <div className="pl-4">
                          <Field
                            name="fileUploads"
                            component={FileUploadForm}
                            documentData={getChangedDocumentsModuleData(
                              Object.keys(changeDoc)[0]
                            )}
                            resolvedFields={_get(
                              row.original.uploadChangeDocument,
                              "resolvedFields"
                            )}
                            onlyPreview
                            showDocumentLabel
                            addReviewComment={
                              !isCompanyDashboard && !isViewMode
                            }
                            hideResolveAllCheckbox={isViewMode ? true : false}
                            handleSaveComment={handleSaveComment}
                            formName="uploadChangeDocuments"
                            formHelperKey="ManagerCompanyDocument"
                            updateEffectKey={companyOfficialEffectKey}
                            updateEffectPath={`${officialSliceName}.uploadChangeDocument`}
                            formId={_get(
                              row.original.uploadChangeDocuments[changeType][
                                Object.keys(changeDoc)[0]
                              ]["templateFile"],
                              "_id"
                            )}
                            resolveAllFieldsCheckbox
                            editorKey={
                              changeDoc[Object.keys(changeDoc)[0]][
                                "templateFile"
                              ]["fileName"]
                            }
                            documentName="Consent To Act"
                            comments={changeDocumentsComments.filter(
                              (comment) =>
                                comment.formName === "uploadChangeDocuments"
                            )}
                            editorParams={{
                              permissions: {
                                edit: isReviewMode ? false : true,
                                review: isReviewMode ? true : false,
                                comment: true,
                                editCommentAuthorOnly: true,
                                deleteCommentAuthorOnly: true,
                              },
                              metadata: {
                                isFromChangesModule: "isFromChangesModule",
                                fileName:
                                  changeDoc[Object.keys(changeDoc)[0]]?.[
                                    "templateFile"
                                  ]?.fileName,
                              },
                            }}
                          />
                        </div>
                      )}
                    </Formik>
                  </div>
                </div>
              </>
            )}

            {/* Signed document upload section */}
            {(changeDoc[Object.keys(changeDoc)[0]]["templateFile"][
              "modeType"
            ] === "pre-signed-documents" ||
              (changeDoc[Object.keys(changeDoc)[0]]["templateFile"][
                "modeType"
              ] === "not-signed-documents" &&
                changeDoc[Object.keys(changeDoc)[0]]?.["signedPdf"])) && (
              <div className="w-full">
                <div className="px-2">Signed version</div>
                <div className="p-3 mt-2 bg-white">
                  <Formik
                    enableReinitialize
                    initialValues={{
                      fileUploads: {
                        [Object.keys(changeDoc)[0]]:
                          changeDoc[Object.keys(changeDoc)[0]]["templateFile"][
                            "modeType"
                          ] === "pre-signed-documents"
                            ? changeDoc[Object.keys(changeDoc)[0]][
                                "templateFile"
                              ]["fileName"]
                            : changeDoc[Object.keys(changeDoc)[0]]["signedPdf"],
                      },
                    }}
                    onSubmit={() => {}}
                  >
                    {() => (
                      <div className="pl-4">
                        <Form>
                          <Field
                            name="fileUploads"
                            component={FileUploadForm}
                            documentData={getChangedDocumentsModuleData(
                              Object.keys(changeDoc)[0]
                            )}
                            resolvedFields={_get(
                              row.original.uploadChangeDocument,
                              "resolvedFields"
                            )}
                            onlyPreview
                            showDocumentLabel
                            addReviewComment={
                              !isCompanyDashboard && !isViewMode
                            }
                            hideResolveAllCheckbox={isViewMode ? true : false}
                            handleSaveComment={handleSaveComment}
                            formName="uploadChangeDocuments"
                            formHelperKey="ManagerCompanyDocument"
                            updateEffectKey={companyOfficialEffectKey}
                            updateEffectPath={`${officialSliceName}.uploadChangeDocument`}
                            formId={_get(
                              row.original.uploadChangeDocuments[changeType][
                                Object.keys(changeDoc)[0]
                              ]["templateFile"],
                              "_id"
                            )}
                            resolveAllFieldsCheckbox
                            editorKey={
                              changeDoc[Object.keys(changeDoc)[0]][
                                "templateFile"
                              ]["fileName"]
                            }
                            documentName="Consent To Act"
                            comments={changeDocumentsComments.filter(
                              (comment) =>
                                comment.formName === "uploadChangeDocuments"
                            )}
                          />
                        </Form>
                      </div>
                    )}
                  </Formik>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default ChangeDocuments;
